import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import {
  clearTokens,
  getAccessToken,
  getOrCreateDeviceId,
  getOrganizationId,
  getRefreshToken,
  saveTokens,
} from './tokenStorage';
import type { ApiEnvelope } from '@/types/api';
import type { LoginResult } from '@/types/auth';
import { markRequestStart, logResponse, logError } from './apiLogger';

const baseURL = import.meta.env.VITE_API_BASE_URL;

export const httpClient = axios.create({ baseURL });

httpClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  config.headers['X-Device-Id'] = getOrCreateDeviceId();
  return markRequestStart(config);
});

// Shared across all callers so a burst of 401s (multiple in-flight
// requests when the access token expires) triggers exactly one
// /auth/refresh call, not one per failed request.
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  const organizationId = getOrganizationId();
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await axios.post<ApiEnvelope<LoginResult>>(
      `${baseURL}/auth/refresh`,
      { refresh_token: refreshToken, organization_id: organizationId },
    );
    const result = response.data.data;
    if (!result?.access_token || !result.refresh_token) {
      return null;
    }
    saveTokens({
      accessToken: result.access_token,
      refreshToken: result.refresh_token,
      organizationId: organizationId ?? 0,
    });
    return result.access_token;
  } catch {
    return null;
  }
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
  _retryCount?: number;
}

const MAX_TRANSIENT_RETRIES = 2;

function transientRetryDelayMs(attempt: number): number {
  // Full jitter — same shape as services/retry.ts's withRetry(), inlined
  // here since this is a single call site re-invoking httpClient(config)
  // rather than a generic function to wrap.
  return Math.random() * Math.min(2000, 300 * 2 ** (attempt - 1));
}

httpClient.interceptors.response.use(
  (response) => {
    logResponse(response);
    return response;
  },
  async (error: AxiosError<ApiEnvelope<unknown>>) => {
    logError(error);

    const config = error.config as RetryableConfig | undefined;
    const status = error.response?.status;
    const isAuthEndpoint = config?.url?.includes('/auth/login') || config?.url?.includes('/auth/refresh');

    // Retry — idempotent GETs only, on a network failure or 5xx (never
    // 4xx, which is never fixed by trying again). Bounded and jittered
    // so a real outage doesn't turn one slow request into a retry storm.
    const isGetRequest = (config?.method ?? 'get').toLowerCase() === 'get';
    const isTransientFailure = !error.response || status! >= 500;
    const retryCount = config?._retryCount ?? 0;
    if (config && isGetRequest && isTransientFailure && retryCount < MAX_TRANSIENT_RETRIES) {
      config._retryCount = retryCount + 1;
      await new Promise((resolve) => setTimeout(resolve, transientRetryDelayMs(config._retryCount!)));
      return httpClient(config);
    }

    if (status === 401 && config && !config._retried && !isAuthEndpoint) {
      config._retried = true;

      refreshPromise ??= refreshAccessToken().finally(() => {
        refreshPromise = null;
      });

      const newAccessToken = await refreshPromise;
      if (newAccessToken) {
        config.headers.Authorization = `Bearer ${newAccessToken}`;
        return httpClient(config);
      }

      clearTokens();
      window.location.assign('/login');
    }

    return Promise.reject(error);
  },
);

/**
 * Unwraps the {success,data,error} envelope and throws on failure.
 * `data` can legitimately be `null` on success (e.g. a 200 response from
 * a DELETE) — only `success: false` is an error, so callers that expect
 * no payload should instantiate this as `unwrap<null>(...)`.
 */
export async function unwrap<T>(promise: Promise<{ data: ApiEnvelope<T> }>): Promise<T> {
  const response = await promise;
  if (!response.data.success) {
    throw new ApiRequestError(response.data.error?.message ?? 'Request failed.', response.data.error?.details, response.data.error?.code);
  }
  return response.data.data as T;
}

/** Same as unwrap(), but also returns `meta` (page/per_page/total/...) for paginated list endpoints. */
export async function unwrapPaginated<T>(
  promise: Promise<{ data: ApiEnvelope<T[]> }>,
): Promise<{ data: T[]; meta: NonNullable<ApiEnvelope<T[]>['meta']> }> {
  const response = await promise;
  if (!response.data.success || !response.data.meta) {
    throw new ApiRequestError(response.data.error?.message ?? 'Request failed.', response.data.error?.details);
  }
  return { data: response.data.data ?? [], meta: response.data.meta };
}

export class ApiRequestError extends Error {
  details?: Record<string, string[]>;
  code?: string;

  constructor(message: string, details?: Record<string, string[]>, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.details = details;
    this.code = code;
  }
}
